import { Router, watchBusy } from "@frui.ts/screens/dist";
import { Modules } from "config";
import type AccountBalanceCheckRequestDetailResponse from "entities/accountBalanceCheckRequestDetailResponse";
import type { interfaces } from "inversify";
import { action } from "mobx";
import AccountingBalanceRepository from "repositories/accountingBalanceRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import DetailViewModel from "viewModels/detailViewModel";
import download from "downloadjs";
import AccountBalanceCheckRequestStatus from "entities/accountBalanceCheckRequestStatus";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";

@Router.registerRoute({ route: ":balanceCheckRequestId", name: Router.Self })
export default class BalanceDetailViewModel extends DetailViewModel<AccountBalanceCheckRequestDetailResponse> {
  readonly modelName = "balance";

  constructor(
    protected itemId: string,
    private repository: AccountingBalanceRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService
  ) {
    super(itemId);
  }

  @watchBusy
  protected async loadDetail() {
    return this.repository.get(this.itemId);
  }

  @action.bound
  getStatusTextClass(status: AccountBalanceCheckRequestStatus): string {
    switch (status) {
      case AccountBalanceCheckRequestStatus.ToBeProcessed:
        return "text-warning font-weight-bold";
      case AccountBalanceCheckRequestStatus.Processed:
        return "text-success font-weight-bold";
      case AccountBalanceCheckRequestStatus.ProcessingFailed:
        return "text-danger font-weight-bold";
      default:
        return "text-muted font-weight-bold";
    }
  }

  @action.bound
  @watchBusy
  async onDownloadResult() {
    const fileResponse = await this.repository.getResultFile(this.itemId);
    const resultFileNamePrefix = this.localizationService.translateModule(Modules.finance, "inventory.balance.resultFileName");
    const defaultFileName = `${resultFileNamePrefix} ${this.item.accountingPeriodTo.replace("-", "")}.xlsx`;
    download(fileResponse.data, parseFileNameFromAxiosResponse(fileResponse, defaultFileName));
  }

  @action.bound
  onClose() {
    this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.balance.detail.${code}`, size);
  }

  translateStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.balance.statusTypes.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new BalanceDetailViewModel(
        itemId,
        container.get(AccountingBalanceRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService)
      );
    };
  }
}
